import React, { useEffect, useRef, useState, useCallback } from 'react';
import jsQR from 'jsqr';
import { X, Camera, CheckCircle, AlertCircle, ScanLine } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getCurrentUser } from '../../profile/services';
import { apiCall } from '../../services/apiUtil';
import toast from 'react-hot-toast';
import { btn } from '../../utils/buttonStyles';

/**
 * PlayerQRScanModal
 *
 * Lets a player scan their own profile QR code to mark themselves as available
 * for an event. Uses native getUserMedia + jsQR (no third-party scanner lib).
 *
 * Props:
 *   isOpen           - boolean
 *   onClose          - () => void
 *   event            - event object  { id }
 *   onAttendanceMarked - () => void  called after successful API update
 */
const PlayerQRScanModal = ({ isOpen, onClose, event, onAttendanceMarked }) => {
  const { theme } = useTheme();
  const currentUser = getCurrentUser();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | starting | scanning | success | error | saving
  const [errorMsg, setErrorMsg] = useState('');

  // ── Start camera ──────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setStatus('starting');
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus('scanning');
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Camera access denied. Click the camera icon in the address bar to allow access.'
        : err.name === 'NotFoundError'
          ? 'No camera found on this device.'
          : `Camera error: ${err.message}`;
      setErrorMsg(msg);
      setStatus('error');
    }
  }, []);

  // ── Stop camera + RAF loop ────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // A ref, so the scan loop always calls the latest handleQRCode rather than
  // the one captured when the loop was created.
  const handleQRCodeRef = useRef(null);

  const handleQRCode = useCallback(async (data) => {
    // Stop scanning immediately
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Extract userId from URL: /player/<uuid>  or  https://…/player/<uuid>
    const match = data.match(/\/player\/([a-zA-Z0-9_-]+)/);
    if (!match) {
      setErrorMsg('Invalid QR code. Please scan your profile QR code.');
      setStatus('error');
      return;
    }

    const scannedUserId = match[1];
    const currentUserId = currentUser?.id ? String(currentUser.id) : null;

    if (!currentUserId || scannedUserId !== currentUserId) {
      setErrorMsg('This QR code belongs to a different user. Please scan your own profile QR code.');
      setStatus('error');
      return;
    }

    // Mark attendance via API
    setStatus('saving');
    try {
      await apiCall(`/events/${event.id}/responses`, {
        method: 'POST',
        body: JSON.stringify({ status: 'available' })
      });

      setStatus('success');
      toast.success('Attendance marked! You are now available.');
      if (onAttendanceMarked) onAttendanceMarked();

      // Auto-close after 2 s
      setTimeout(() => {
        stopCamera();
        onClose();
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to save attendance. Please try again.');
      setStatus('error');
    }
  }, [currentUser, event, onAttendanceMarked, onClose, stopCamera]);

  // Keep ref in sync
  handleQRCodeRef.current = handleQRCode;

  // ── Scan loop ─────────────────────────────────────────────────────────────
  const scanFrameStable = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrameStable);
      return;
    }

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert'
    });

    if (code) {
      handleQRCodeRef.current(code.data);
      return;
    }

    rafRef.current = requestAnimationFrame(scanFrameStable);
  }, []);

  // Start scan loop once camera is ready
  useEffect(() => {
    if (status === 'scanning') {
      rafRef.current = requestAnimationFrame(scanFrameStable);
    }
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [status, scanFrameStable]);

  // Lifecycle: open/close
  useEffect(() => {
    if (isOpen) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    setErrorMsg('');
    startCamera();
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className={`${theme.card} rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-5 py-4 border-b ${theme.border} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Camera size={20} className="text-blue-500" />
            <h2 className={`font-bold text-lg ${theme.text}`}>Scan Your QR Code</h2>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <p className={`text-sm ${theme.textSecondary} mb-4 text-center`}>
            Show your profile QR code to the camera to mark yourself as available
          </p>

          {/* Camera viewport */}
          <div className="relative rounded-xl overflow-hidden bg-black aspect-square mb-4">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
            />
            {/* Hidden canvas for jsQR */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Scanning overlay */}
            {status === 'scanning' && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Corner brackets */}
                <div className="absolute top-5 left-5 w-8 h-8 border-t-4 border-l-4 border-blue-400 rounded-tl-sm" />
                <div className="absolute top-5 right-5 w-8 h-8 border-t-4 border-r-4 border-blue-400 rounded-tr-sm" />
                <div className="absolute bottom-5 left-5 w-8 h-8 border-b-4 border-l-4 border-blue-400 rounded-bl-sm" />
                <div className="absolute bottom-5 right-5 w-8 h-8 border-b-4 border-r-4 border-blue-400 rounded-br-sm" />
                {/* Scan line */}
                <div className="absolute inset-x-10 scan-line h-0.5 bg-blue-400 shadow-[0_0_8px_2px_rgba(96,165,250,0.8)]" />
              </div>
            )}

            {/* Starting spinner */}
            {status === 'starting' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
                <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-white text-sm">Starting camera…</p>
              </div>
            )}

            {/* Success overlay */}
            {status === 'success' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-green-900/80">
                <CheckCircle size={56} className="text-green-400 mb-3" />
                <p className="text-white font-semibold text-lg">Attendance Marked!</p>
                <p className="text-green-300 text-sm mt-1">You are now available</p>
              </div>
            )}

            {/* Saving overlay */}
            {status === 'saving' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
                <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-white text-sm">Saving attendance…</p>
              </div>
            )}
          </div>

          {/* Error state */}
          {status === 'error' && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Status bar */}
          {status === 'scanning' && (
            <div className="flex items-center justify-center gap-2">
              <ScanLine size={16} className="text-blue-500" />
              <span className={`text-sm font-medium ${theme.text}`}>Ready — point camera at your QR code</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-4">
            {status === 'error' && (
              <button
                onClick={handleRetry}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
              >
                Try Again
              </button>
            )}
            <button
              onClick={handleClose}
              className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
            >
              {status === 'success' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scanMove {
          0%, 100% { top: 20%; }
          50%       { top: 75%; }
        }
        .scan-line {
          animation: scanMove 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PlayerQRScanModal;
